import { useState, useEffect, useCallback } from 'react'
import { authedFetch } from '../api'
import { Link } from 'react-router-dom'
import { useAppDispatch } from '../hooks'
import { deleteFailure } from '../slices/failuresSlice'
import type { Failure, FailureAgent, AgentTotal } from '../slices/failuresSlice'
import './FailuresGrid.css'

interface FailuresGridProps {
  failures: Failure[]
  agents: FailureAgent[]
  agentTotals: AgentTotal[]
  tagFilter?: string | null
  searchFilter?: string | null
}

interface SummaryData {
  agents: string[]
  grid: Record<string, Record<string, number>>
  total_with_scores: number
  won_by_non_replay: number
  won_only_by_replay: number
  won_by_none: number
}

function formatTime(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 60) return `${Math.round(seconds)}s`
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  return `${mins}m${secs > 0 ? ` ${secs}s` : ''}`
}

function FailuresGrid({ failures, agents, agentTotals, tagFilter, searchFilter }: FailuresGridProps) {
  const dispatch = useAppDispatch()
  const [summaryExpanded, setSummaryExpanded] = useState(false)
  const [summaryData, setSummaryData] = useState<SummaryData | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [generatingReport, setGeneratingReport] = useState(false)
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null)

  const handleDeleteConfirm = useCallback(async (failureId: string) => {
    await dispatch(deleteFailure(failureId))
    setConfirmingDeleteId(null)
  }, [dispatch])

  const handleDeleteCancel = useCallback(() => {
    setConfirmingDeleteId(null)
  }, [])

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true)
    try {
      const params = new URLSearchParams()
      if (tagFilter) params.set('tag', tagFilter)
      if (searchFilter) params.set('search', searchFilter)
      const res = await fetch(`/api/failures/summary?${params.toString()}`)
      if (res.ok) setSummaryData(await res.json())
    } finally {
      setSummaryLoading(false)
    }
  }, [tagFilter, searchFilter])

  useEffect(() => {
    if (summaryExpanded && !summaryData && !summaryLoading) {
      fetchSummary()
    }
  }, [summaryExpanded, summaryData, summaryLoading, fetchSummary])

  // Re-fetch summary when filters change
  useEffect(() => {
    if (summaryExpanded) {
      setSummaryData(null)
    }
  }, [tagFilter, searchFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  if (agents.length === 0) {
    return <div className="failures-grid-empty">No agents configured.</div>
  }

  const totalsMap = new Map(agentTotals.map((t) => [t.agent_name, t]))

  const handleGenerateReport = async () => {
    setGeneratingReport(true)
    try {
      const res = await authedFetch('/api/reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag: tagFilter || '', search: searchFilter || '' }),
      })
      if (res.ok) {
        const report = await res.json()
        window.open(`/reports/${report.id}`, '_blank')
      }
    } finally {
      setGeneratingReport(false)
    }
  }

  return (
    <div data-testid="failures-grid" className="failures-grid-wrapper">
      <table className="failures-grid">
        <thead>
          <tr>
            <th className="failures-grid-th failures-grid-id-col">ID</th>
            <th className="failures-grid-th failures-grid-title-col">Title</th>
            {agents.map((agent) => (
              <th key={agent.id} className="failures-grid-th failures-grid-agent-col">
                {agent.name}
              </th>
            ))}
            <th className="failures-grid-th failures-grid-actions-col"></th>
          </tr>
        </thead>
        <tbody>
          {agentTotals.length > 0 && (
            <>
              <tr
                className="failures-grid-totals-row"
                onClick={() => setSummaryExpanded(!summaryExpanded)}
              >
                <td className="failures-grid-td failures-grid-id-col failures-grid-totals-label" colSpan={2}>
                  <span className="failures-grid-totals-toggle">{summaryExpanded ? '▾' : '▸'}</span>
                  Summary
                </td>
                {agents.map((agent) => {
                  const t = totalsMap.get(agent.name)
                  if (!t) return <td key={agent.id} className="failures-grid-td failures-grid-agent-col failures-grid-no-data">—</td>
                  const maxScore = t.failure_count * 3
                  const pct = maxScore > 0 ? Math.round(t.total_score / maxScore * 100) : 0
                  return (
                    <td key={agent.id} className="failures-grid-td failures-grid-agent-col">
                      <span className={`failures-grid-score ${pct > 0 ? 'positive' : ''}`}>
                        {pct}%
                      </span>
                    </td>
                  )
                })}
                <td className="failures-grid-td failures-grid-actions-col"></td>
              </tr>
              {summaryExpanded && (
                <>
                  <tr className="failures-grid-totals-detail">
                    <td className="failures-grid-td" colSpan={2}>Pass</td>
                    {agents.map((agent) => {
                      const t = totalsMap.get(agent.name)
                      return <td key={agent.id} className="failures-grid-td failures-grid-agent-col status-pass">{t?.pass_count ?? 0}</td>
                    })}
                    <td className="failures-grid-td"></td>
                  </tr>
                  <tr className="failures-grid-totals-detail">
                    <td className="failures-grid-td" colSpan={2}>Fail</td>
                    {agents.map((agent) => {
                      const t = totalsMap.get(agent.name)
                      return <td key={agent.id} className="failures-grid-td failures-grid-agent-col status-fail">{t?.fail_count ?? 0}</td>
                    })}
                    <td className="failures-grid-td"></td>
                  </tr>
                  <tr className="failures-grid-totals-detail">
                    <td className="failures-grid-td" colSpan={2}>DSQ</td>
                    {agents.map((agent) => {
                      const t = totalsMap.get(agent.name)
                      return <td key={agent.id} className="failures-grid-td failures-grid-agent-col status-dsq">{t?.dsq_count ?? 0}</td>
                    })}
                    <td className="failures-grid-td"></td>
                  </tr>
                  <tr className="failures-grid-totals-detail">
                    <td className="failures-grid-td" colSpan={2}>Disagree</td>
                    {agents.map((agent) => {
                      const t = totalsMap.get(agent.name)
                      return <td key={agent.id} className="failures-grid-td failures-grid-agent-col status-disagree">{t?.disagree_count ?? 0}</td>
                    })}
                    <td className="failures-grid-td"></td>
                  </tr>
                  {summaryLoading && (
                    <tr className="failures-grid-totals-detail">
                      <td className="failures-grid-td" colSpan={3 + agents.length} style={{ textAlign: 'center', padding: '12px' }}>
                        Loading summary data...
                      </td>
                    </tr>
                  )}
                  {summaryData && (
                    <>
                      <tr className="failures-grid-totals-detail">
                        <td className="failures-grid-td" colSpan={3 + agents.length} style={{ paddingTop: '12px', paddingBottom: '4px' }}>
                          <strong>Win/Loss Percentages</strong>
                          <span style={{ marginLeft: '8px', fontWeight: 'normal', color: 'var(--color-text-muted)' }}>
                            ({summaryData.total_with_scores} failures with scores)
                          </span>
                        </td>
                      </tr>
                      <tr className="failures-grid-totals-detail">
                        <td className="failures-grid-td" colSpan={3 + agents.length}>
                          <div className="summary-stats">
                            <span className="summary-stat">
                              <span className="summary-stat-value status-pass">
                                {summaryData.total_with_scores > 0 ? Math.round(summaryData.won_by_non_replay / summaryData.total_with_scores * 100) : 0}%
                              </span>
                              <span className="summary-stat-label">Won by non-Replay agent</span>
                            </span>
                            <span className="summary-stat">
                              <span className="summary-stat-value" style={{ color: 'var(--color-primary, #2563eb)' }}>
                                {summaryData.total_with_scores > 0 ? Math.round(summaryData.won_only_by_replay / summaryData.total_with_scores * 100) : 0}%
                              </span>
                              <span className="summary-stat-label">Won only by Replay agent</span>
                            </span>
                            <span className="summary-stat">
                              <span className="summary-stat-value status-fail">
                                {summaryData.total_with_scores > 0 ? Math.round(summaryData.won_by_none / summaryData.total_with_scores * 100) : 0}%
                              </span>
                              <span className="summary-stat-label">No agent won</span>
                            </span>
                          </div>
                        </td>
                      </tr>
                      {summaryData.agents.length > 1 && (
                        <>
                          <tr className="failures-grid-totals-detail">
                            <td className="failures-grid-td" colSpan={3 + agents.length} style={{ paddingTop: '12px', paddingBottom: '4px' }}>
                              <strong>Agent vs Agent</strong>
                              <span style={{ marginLeft: '8px', fontWeight: 'normal', color: 'var(--color-text-muted)' }}>
                                (columns: winners, rows: losers)
                              </span>
                            </td>
                          </tr>
                          <tr className="failures-grid-totals-detail">
                            <td className="failures-grid-td" colSpan={3 + agents.length}>
                              <div className="summary-grid-wrapper">
                                <table className="summary-grid">
                                  <thead>
                                    <tr>
                                      <th className="summary-grid-corner"></th>
                                      {summaryData.agents.map((a) => (
                                        <th key={a} className="summary-grid-header">{a}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {summaryData.agents.map((loser) => (
                                      <tr key={loser}>
                                        <td className="summary-grid-row-header">{loser}</td>
                                        {summaryData.agents.map((winner) => {
                                          const count = summaryData.grid[winner]?.[loser] ?? 0
                                          return (
                                            <td
                                              key={winner}
                                              className={`summary-grid-cell ${winner === loser ? 'summary-grid-diagonal' : count > 0 ? 'summary-grid-nonzero' : ''}`}
                                            >
                                              {winner === loser ? '—' : count}
                                            </td>
                                          )
                                        })}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        </>
                      )}
                      <tr className="failures-grid-totals-detail">
                        <td className="failures-grid-td" colSpan={3 + agents.length} style={{ paddingTop: '12px', paddingBottom: '12px' }}>
                          <button
                            type="button"
                            className="summary-generate-report-btn"
                            disabled={generatingReport}
                            onClick={(e) => { e.stopPropagation(); handleGenerateReport() }}
                          >
                            {generatingReport ? 'Generating...' : 'Generate Report'}
                          </button>
                        </td>
                      </tr>
                    </>
                  )}
                </>
              )}
            </>
          )}
          {failures.map((failure) => {
            const scoreMap = new Map(
              failure.agent_scores.map((s) => [s.agent_name, s])
            )
            return (
              <tr key={failure.id} data-testid={`failures-grid-row-${failure.id}`} className="failures-grid-row">
                <td className="failures-grid-td failures-grid-id-col">
                  <Link to={`/failures/${failure.id}`} className="failures-grid-link">
                    {failure.short_id}
                  </Link>
                </td>
                <td className="failures-grid-td failures-grid-title-col">
                  <Link to={`/failures/${failure.id}`} className="failures-grid-link failures-grid-title-text failure-card-link">
                    {failure.title}
                  </Link>
                </td>
                {agents.map((agent) => {
                  const score = scoreMap.get(agent.name)
                  const isWinner = failure.winner_agent === agent.name
                  if (!score) {
                    return (
                      <td key={agent.id} className="failures-grid-td failures-grid-agent-col failures-grid-no-data">
                        —
                      </td>
                    )
                  }
                  return (
                    <td
                      key={agent.id}
                      className={`failures-grid-td failures-grid-agent-col ${isWinner ? 'failures-grid-winner' : ''}`}
                    >
                      <span className={`failures-grid-score ${score.score > 0 ? 'positive' : score.score < 0 ? 'negative' : ''}`}>
                        {score.score > 0 ? '+' : ''}{score.score}
                      </span>
                      <span className="failures-grid-time">
                        {formatTime(score.avg_time)}
                      </span>
                    </td>
                  )
                })}
                <td className="failures-grid-td failures-grid-actions-col">
                  {confirmingDeleteId === failure.id ? (
                    <div className="delete-confirm">
                      <span className="delete-confirm-text">Delete Failure?</span>
                      <button className="delete-confirm-yes" onClick={(e) => { e.stopPropagation(); handleDeleteConfirm(failure.id) }}>Yes</button>
                      <button className="delete-confirm-no" onClick={(e) => { e.stopPropagation(); handleDeleteCancel() }}>No</button>
                    </div>
                  ) : (
                    <button className="delete-btn" title="Delete Failure" onClick={(e) => { e.stopPropagation(); setConfirmingDeleteId(failure.id) }}>
                      🗑
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default FailuresGrid
